<template>
  <div id="wc-mobile-ot-switcher" ref="carouselRoot" class="wc-mobile-ot-switcher">
    <div
      v-if="!isDragEnabled"
      class="wc-mobile-ot-switcher__row wc-mobile-ot-switcher__row--centered"
    >
      <button
        v-for="ot in visibleOrderTypes"
        :key="ot.id"
        type="button"
        class="wc-mobile-ot-switcher__tile"
        :class="{ active: isActive(ot) }"
        :aria-current="isActive(ot) ? 'true' : undefined"
        :disabled="switching"
        @click="selectOrderType(ot)"
      >
        <div class="wc-mobile-ot-switcher__icon">
          <WCImage :imageSrc="`${cdn}/api/img/${ot.image}`" :imageAlt="ot.name" />
        </div>
        <span class="wc-mobile-ot-switcher__label">{{ ot.name }}</span>
      </button>
    </div>

    <WCCarousel v-else :isSlider="true">
      <Splide :options="splideOptions" role="list">
        <SplideSlide v-for="ot in visibleOrderTypes" :key="ot.id" role="listitem">
          <button
            type="button"
            class="wc-mobile-ot-switcher__tile"
            :class="{ active: isActive(ot) }"
            :aria-current="isActive(ot) ? 'true' : undefined"
            :disabled="switching"
            @click="selectOrderType(ot)"
          >
            <div class="wc-mobile-ot-switcher__icon">
              <WCImage :imageSrc="`${cdn}/api/img/${ot.image}`" :imageAlt="ot.name" />
            </div>
            <span class="wc-mobile-ot-switcher__label">{{ ot.name }}</span>
          </button>
        </SplideSlide>
      </Splide>
    </WCCarousel>
  </div>
</template>

<script setup lang="ts">
import { inject, computed, ref, onMounted, onUnmounted } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import WCCarousel from '@/components/WCCarousel/WCCarousel.vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import { configurationKey } from '@/injections';
import type OrderType from '@/models/entities/cart/order-type';

const { visibleOrderTypes, currentOrderType, navigateToOrderTypeHome } = useOrderTypes();
const $configuration = inject(configurationKey);
const carouselRoot = ref<HTMLElement | null>(null);
const switching = ref(false);

const cdn = computed(() => $configuration?.cdn ?? '');
const orderTypeCount = computed(() => visibleOrderTypes.value?.length ?? 0);
const containerWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 375);
const tileStride = ref(84);

const isDragEnabled = computed(() => {
  const totalTilesWidth = orderTypeCount.value * tileStride.value;
  return totalTilesWidth > (containerWidth.value - 32);
});

const measureDimensions = () => {
  const container = carouselRoot.value;
  if (!container) return;
  const measuredContainerWidth = container.getBoundingClientRect().width;
  if (measuredContainerWidth > 0) containerWidth.value = measuredContainerWidth;
  const tiles = container.querySelectorAll<HTMLElement>('.wc-mobile-ot-switcher__tile');
  if (tiles.length >= 2) {
    const stride = tiles[1].getBoundingClientRect().left - tiles[0].getBoundingClientRect().left;
    if (stride > 0) tileStride.value = stride;
  } else if (tiles.length === 1) {
    const tileWidth = tiles[0].getBoundingClientRect().width;
    if (tileWidth > 0) tileStride.value = tileWidth;
  }
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  measureDimensions();
  if (typeof ResizeObserver !== 'function' || !carouselRoot.value) return;
  resizeObserver = new ResizeObserver(measureDimensions);
  resizeObserver.observe(carouselRoot.value);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

const splideOptions = computed(() => ({
  autoWidth: true,
  arrows: false,
  pagination: false,
  gap: '0.75rem',
  drag: 'free' as const,
  padding: {
    left: '1rem',
    right: '1rem',
  },
}));

const isActive = (ot: OrderType): boolean => currentOrderType.value?.id === ot.id;

const selectOrderType = async (ot: OrderType): Promise<void> => {
  switching.value = true;
  try {
    await navigateToOrderTypeHome(ot.id);
  } catch (error) {
    console.error('Failed to switch order type:', error);
  } finally {
    switching.value = false;
  }
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-mobile-ot-switcher {
  background: var(--secondary-lighter-9, get-color('secondary', 'lighter-9'));
  padding: convert-px-to-rem(24) 0 convert-px-to-rem(8);
}

.wc-mobile-ot-switcher__row {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: convert-px-to-rem(12);
  padding-left: convert-px-to-rem(16);
  scrollbar-width: none;
  background: var(--secondary-lighter-9, get-color('secondary', 'lighter-9'));

  &::-webkit-scrollbar {
    display: none;
  }

  &--centered {
    justify-content: center;
    gap: convert-px-to-rem(24);
    overflow-x: visible;
    padding-right: convert-px-to-rem(16);
  }
}

.wc-mobile-ot-switcher :deep(.splide__list) {
  align-items: flex-start;
}

.wc-mobile-ot-switcher__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: convert-px-to-rem(5);
  min-width: convert-px-to-rem(72);
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s;
  background: none;
  border: none;
  padding: 0;

  &.active {
    opacity: 1;

    .wc-mobile-ot-switcher__icon {
      box-shadow: 0 convert-px-to-rem(6) convert-px-to-rem(16) rgba(0, 0, 0, 0.28);
    }

    .wc-mobile-ot-switcher__label {
      color: var(--dark, $dark);
      font-weight: 700;
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.wc-mobile-ot-switcher__icon {
  width: convert-px-to-rem(72);
  height: convert-px-to-rem(72);
  border-radius: convert-px-to-rem(12);
  background: var(--white, $white);
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: none;
  transition: box-shadow 0.15s;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.wc-mobile-ot-switcher__label {
  line-height: 1.2;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: convert-px-to-rem(12);
  font-weight: 400;
  text-align: center;
  max-width: convert-px-to-rem(72);
  color: var(--dark-lighter-2, get-color('dark', 'lighter-2'));
}
</style>
